import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { loadMe } from '@blimo/api-client/react';
import { adminNavItems } from './nav.js';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, TwoFactorChallengePage } from './routes/auth-pages.jsx';
import { ProtectedLayout } from './routes/protected-layout.jsx';
import { NotFoundPage, PlaceholderPage, ProfilePage, RequirePermission } from './routes/pages.jsx';
import { DashboardPage } from './routes/dashboard.jsx';
import { PropertiesListPage } from './routes/properties/list.jsx';
import { PropertyDetailPage } from './routes/properties/detail.jsx';
import { UnitsListPage } from './routes/units/list.jsx';
import { UnitDetailPage } from './routes/units/detail.jsx';
import { PartiesListPage } from './routes/parties/list.jsx';
import { PartyDetailPage } from './routes/parties/detail.jsx';
import { PartyCreatePage, PartyEditPage } from './routes/parties/form.jsx';
import { PartyImportDetailPage, PartyImportsPage } from './routes/parties/import.jsx';
import { UsersListPage } from './routes/users/list.jsx';
import { UserDetailPage } from './routes/users/detail.jsx';
import { OpenItemsPage, CostsPage, RentDevelopmentPage } from './routes/finance/pages.jsx';
import { ImportRunsPage } from './routes/imports/list.jsx';
import { DocumentChecksListPage } from './routes/document-checks/list.jsx';
import { DocumentCheckDetailPage } from './routes/document-checks/detail.jsx';
import { AnnouncementsListPage } from './routes/communication/announcements/list.jsx';
import { AnnouncementCreatePage, AnnouncementEditPage } from './routes/communication/announcements/form.jsx';
import { AnnouncementDetailPage } from './routes/communication/announcements/detail.jsx';
import { InboxListPage } from './routes/communication/inbox/list.jsx';
import { InboxThreadPage } from './routes/communication/inbox/detail.jsx';
import { OutboundListPage } from './routes/communication/outbound/list.jsx';
import { OutboundDetailPage } from './routes/communication/outbound/detail.jsx';
import { MailTemplatesPage } from './routes/communication/templates/list.jsx';
import { MailTemplateEditorPage } from './routes/communication/templates/editor.jsx';
import { MeterReadingsPage } from './routes/meters/readings.jsx';
import { MetersListPage } from './routes/meters/list.jsx';
import { MeterDetailPage } from './routes/meters/detail.jsx';
import { MeterReadersPage } from './routes/meters/readers.jsx';
import { ProxyAuthorizationsPage } from './routes/owner-meetings/proxies.jsx';
import { LettingTicketsPage } from './routes/lettings/list.jsx';
import { LinksAdminPage } from './routes/administration/links.jsx';
import { AuditLogPage } from './routes/administration/audit-log.jsx';
import { RolesPage } from './routes/administration/roles.jsx';
import { DuplicatesPage, DuplicateDetailPage } from './routes/administration/duplicates.jsx';
import { SettingsPage } from './routes/administration/settings.jsx';
import { CommentsModerationPage } from './routes/communication/comments.jsx';
import { DocumentFormsPage } from './routes/documents/forms.jsx';
import { TenanciesListPage } from './routes/tenancies/list.jsx';
import { TenancyDetailPage } from './routes/tenancies/detail.jsx';
import { MandatesListPage } from './routes/mandates/list.jsx';
import { MandateDetailPage } from './routes/mandates/detail.jsx';
import { financeSearch } from '@blimo/shared';
import { listSearch } from './lib/list-search.js';
import { redirectSearch, safeRedirect } from './lib/redirect.js';

/**
 * Code-based route tree.
 * @param {{ queryClient: import('@tanstack/react-query').QueryClient, api: import('@blimo/api-client').BlimoApi, history?: import('@tanstack/react-router').RouterHistory }} options
 */
export function createAppRouter({ queryClient, api, history }) {
  const rootRoute = createRootRouteWithContext()({
    component: Outlet,
    notFoundComponent: NotFoundPage,
  });

  // --- public auth pages -------------------------------------------------
  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    validateSearch: redirectSearch,
    beforeLoad: async ({ context, search }) => {
      const result = await loadMe(context).catch(() => null);
      if (result?.status === 'ok') throw redirect({ href: safeRedirect(search.redirect) });
    },
    component: LoginPage,
  });

  const twoFactorChallengeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/two-factor-challenge',
    validateSearch: redirectSearch,
    component: TwoFactorChallengePage,
  });

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    component: ForgotPasswordPage,
  });

  const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password/$token',
    validateSearch: (search) => ({ email: typeof search.email === 'string' ? search.email : undefined }),
    component: ResetPasswordPage,
  });

  // --- protected application ---------------------------------------------
  const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'app',
    beforeLoad: async ({ context, location }) => {
      const result = await loadMe(context);
      if (result.status === 'unauthenticated') {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      // 'two-factor-required' and missing app access are rendered by ProtectedLayout.
      return { auth: result };
    },
    component: ProtectedLayout,
  });

  const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage });

  const profileRoute = createRoute({
    getParentRoute: () => appRoute,
    path: '/profil',
    validateSearch: (search) => ({ tab: typeof search.tab === 'string' ? search.tab : undefined }),
    component: ProfilePage,
  });

  /** @param {string} path @param {string} permission @param {import('react').ComponentType} Page @param {Record<string, any>} [extra] */
  const page = (path, permission, Page, extra = {}) =>
    createRoute({
      getParentRoute: () => appRoute,
      path,
      ...extra,
      component: function GuardedPage() {
        return (
          <RequirePermission permission={permission}>
            <Page />
          </RequirePermission>
        );
      },
    });

  const tabSearch = (search) => ({ tab: typeof search.tab === 'string' ? search.tab : undefined });

  const featureRoutes = [
    page('/liegenschaften', 'properties.view', PropertiesListPage, { validateSearch: listSearch(['q', 'portal', 'archived']) }),
    page('/liegenschaften/$propertyId', 'properties.view', PropertyDetailPage, { validateSearch: tabSearch }),
    page('/einheiten', 'units.view', UnitsListPage, { validateSearch: listSearch(['q', 'type', 'property']) }),
    page('/einheiten/$unitId', 'units.view', UnitDetailPage, { validateSearch: tabSearch }),
    page('/personen', 'parties.view', PartiesListPage, { validateSearch: listSearch(['q', 'type', 'role', 'source']) }),
    page('/personen/neu', 'parties.create', PartyCreatePage),
    page('/personen/import', 'parties.import', PartyImportsPage),
    page('/personen/import/$importId', 'parties.import', PartyImportDetailPage, { validateSearch: listSearch(['status']) }),
    page('/personen/$partyId', 'parties.view', PartyDetailPage),
    page('/personen/$partyId/bearbeiten', 'parties.update', PartyEditPage),
    page('/administration/benutzer', 'users.view', UsersListPage, { validateSearch: listSearch(['q', 'status', 'role', 'access']) }),
    page('/administration/benutzer/$userId', 'users.view', UserDetailPage),
    page('/finanzen/offene-posten', 'finance.view', OpenItemsPage, { validateSearch: financeSearch }),
    page('/finanzen/kosten', 'finance.view', CostsPage, { validateSearch: financeSearch }),
    page('/finanzen/mietentwicklung', 'finance.view', RentDevelopmentPage, { validateSearch: financeSearch }),
    page('/finanzen/mandanten', 'finance.view', MandatesListPage, { validateSearch: listSearch(['q', 'archived']) }),
    page('/finanzen/mandanten/$clientId', 'finance.view', MandateDetailPage, { validateSearch: tabSearch }),
    page('/administration/importe', 'import-runs.view', ImportRunsPage, { validateSearch: listSearch(['source', 'status']) }),
    page('/belegpruefung', 'document-checks.view', DocumentChecksListPage, { validateSearch: listSearch(['property', 'year']) }),
    page('/belegpruefung/$checkId', 'document-checks.view', DocumentCheckDetailPage, { validateSearch: tabSearch }),
    page('/kommunikation/mitteilungen', 'announcements.view', AnnouncementsListPage, { validateSearch: listSearch(['q', 'status', 'property']) }),
    page('/kommunikation/mitteilungen/neu', 'announcements.manage', AnnouncementCreatePage, {
      validateSearch: (search) => ({ property: /^\d+$/.test(String(search.property ?? '')) ? String(search.property) : undefined }),
    }),
    page('/kommunikation/mitteilungen/$announcementId', 'announcements.view', AnnouncementDetailPage),
    page('/kommunikation/mitteilungen/$announcementId/bearbeiten', 'announcements.manage', AnnouncementEditPage),
    page('/kommunikation/posteingang', 'messages.view', InboxListPage, { validateSearch: listSearch(['q', 'status', 'mine', 'property']) }),
    page('/kommunikation/posteingang/$threadId', 'messages.view', InboxThreadPage),
    page('/kommunikation/versandhistorie', 'outbound-messages.view', OutboundListPage, {
      validateSearch: listSearch(['q', 'source', 'status', 'from', 'to']),
    }),
    page('/kommunikation/versandhistorie/$messageId', 'outbound-messages.view', OutboundDetailPage),
    page('/kommunikation/vorlagen', 'mail-templates.manage', MailTemplatesPage),
    page('/kommunikation/vorlagen/$templateKey', 'mail-templates.manage', MailTemplateEditorPage),
    page('/zaehler/staende', 'meters.view', MeterReadingsPage, {
      validateSearch: listSearch(['year', 'status', 'property', 'supplier', 'reader', 'search']),
    }),
    page('/zaehler', 'meters.view', MetersListPage, { validateSearch: listSearch(['q', 'property', 'supplier', 'archived', 'tab']) }),
    page('/zaehler/ableser', 'meters.view', MeterReadersPage, { validateSearch: listSearch(['year']) }),
    page('/zaehler/$meterId', 'meters.view', MeterDetailPage),
    page('/eigentuemerversammlung/vollmachten', 'proxy-authorizations.view', ProxyAuthorizationsPage, {
      validateSearch: listSearch(['property', 'from', 'to', 'confirmed']),
    }),
    page('/mietverhaeltnisse', 'tenancies.view', TenanciesListPage, { validateSearch: listSearch(['q', 'status', 'property']) }),
    page('/mietverhaeltnisse/$tenancyId', 'tenancies.view', TenancyDetailPage, { validateSearch: tabSearch }),
    page('/vermietung', 'tenancies.view', LettingTicketsPage, { validateSearch: listSearch(['property', 'open', 'status', 'removed']) }),
    page('/administration/links', 'links.manage', LinksAdminPage),
    page('/administration/einstellungen', 'settings.manage', SettingsPage, { validateSearch: tabSearch }),
    page('/administration/aenderungsprotokoll', 'audit-log.view', AuditLogPage, {
      validateSearch: listSearch(['q', 'subject_type', 'subject_id', 'causer', 'log', 'event', 'from', 'to', 'entry']),
    }),
    page('/administration/rollen', 'roles.manage', RolesPage),
    page('/administration/dubletten', 'parties.update', DuplicatesPage, { validateSearch: listSearch(['q', 'status', 'type']) }),
    page('/administration/dubletten/$issueId', 'parties.update', DuplicateDetailPage),
    page('/kommunikation/kommentare', 'comments.moderate', CommentsModerationPage, {
      validateSearch: listSearch(['q', 'property', 'year', 'status', 'kind', 'replies', 'author']),
    }),
    page('/dokumente/formulare', 'documents.view', DocumentFormsPage),
  ];
  const implemented = new Set(featureRoutes.map((r) => r.options.path));

  const placeholderRoutes = adminNavItems
    .filter((item) => item.to !== '/' && !implemented.has(item.to))
    .map((item) =>
      createRoute({
        getParentRoute: () => appRoute,
        path: item.to,
        component: function NavPlaceholder() {
          return <PlaceholderPage item={item} />;
        },
      }),
    );

  const routeTree = rootRoute.addChildren([
    loginRoute,
    twoFactorChallengeRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    appRoute.addChildren([dashboardRoute, profileRoute, ...featureRoutes, ...placeholderRoutes]),
  ]);

  return createRouter({
    routeTree,
    history,
    context: { queryClient, api },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}
